import React, { useState, useEffect, useRef } from 'react';
import { X, HelpCircle, Upload, Loader } from 'lucide-react';
import type { AnalyticsEntry } from '../../../types/analytics';
import { METRIC_TOOLTIPS } from '../../../types/analytics';
import { analyzeScreenshot } from '../../../services/openai';
import toast from 'react-hot-toast';

interface AnalyticsFormProps {
  entry?: AnalyticsEntry | null;
  onSubmit: (data: Omit<AnalyticsEntry, 'id' | 'date'>) => void;
  onClose: () => void;
}

export default function AnalyticsForm({ entry, onSubmit, onClose }: AnalyticsFormProps) {
  const [formData, setFormData] = useState({
    platform: entry?.platform || 'youtube',
    contentName: entry?.contentName || '',
    views: entry?.views || 0,
    likes: entry?.likes || 0,
    comments: entry?.comments || 0,
    screenshot: null as File | null,
    screenshotPreview: entry?.screenshotPreview || '',
    aiAnalysis: ''
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Calculate engagement rate automatically
  const engagementRate = formData.views > 0
    ? ((formData.likes + formData.comments) / formData.views) * 100
    : 0;

  const formatNumber = (value: string): string => {
    const cleanValue = value.replace(/\D/g, '');
    return cleanValue ? parseInt(cleanValue).toLocaleString('pt-BR') : '';
  };

  const unformatNumber = (value: string): number => {
    return parseInt(value.replace(/\./g, '')) || 0;
  };

  const handleNumberInput = (field: 'views' | 'likes' | 'comments', value: string) => {
    const formattedValue = formatNumber(value);
    const numericValue = unformatNumber(formattedValue);
    setFormData(prev => ({ ...prev, [field]: numericValue }));
  };

  const analyzeImage = async (file: File) => {
    try {
      setIsAnalyzing(true);
      
      // Create a new FileReader
      const reader = new FileReader();
      
      // Set up the onload handler before calling readAsDataURL
      reader.onload = async () => {
        try {
          const base64Image = reader.result as string;
          const analysis = await analyzeScreenshot(base64Image);
          
          setFormData(prev => ({ 
            ...prev, 
            aiAnalysis: analysis 
          }));
          
          toast.success('Análise da imagem concluída!');
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Erro ao analisar imagem';
          console.error('Error analyzing image:', message);
          toast.error(message);
        } finally {
          setIsAnalyzing(false);
        }
      };

      // Handle potential errors during file reading
      reader.onerror = () => {
        console.error('Error reading file:', reader.error);
        toast.error('Erro ao ler arquivo');
        setIsAnalyzing(false);
      };

      // Start reading the file
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error setting up file analysis:', error);
      toast.error('Erro ao processar arquivo');
      setIsAnalyzing(false);
    }
  };

  const handleFileChange = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione apenas arquivos de imagem.');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('O arquivo é muito grande. O tamanho máximo é 10MB.');
      return;
    }

    try {
      // Create object URL for preview
      const previewUrl = URL.createObjectURL(file);
      
      setFormData(prev => ({
        ...prev,
        screenshot: file,
        screenshotPreview: previewUrl
      }));

      // Start analysis
      await analyzeImage(file);
    } catch (error) {
      console.error('Error handling file:', error);
      toast.error('Erro ao processar imagem');
      
      // Clean up on error
      setFormData(prev => ({
        ...prev,
        screenshot: null,
        screenshotPreview: '',
        aiAnalysis: ''
      }));
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      await handleFileChange(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      engagementRate
    });
  };

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (formData.screenshotPreview && !entry?.screenshotPreview) {
        URL.revokeObjectURL(formData.screenshotPreview);
      }
    };
  }, [formData.screenshotPreview, entry]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-lg w-full mx-4">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {entry ? 'Editar Dados' : 'Adicionar Dados'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plataforma
            </label>
            <select
              value={formData.platform}
              onChange={(e) => setFormData({ ...formData, platform: e.target.value as any })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
            >
              <option value="youtube">YouTube</option>
              <option value="instagram">Instagram</option>
              <option value="tiktok">TikTok</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome do Conteúdo
            </label>
            <input
              type="text"
              value={formData.contentName}
              onChange={(e) => setFormData({ ...formData, contentName: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
              placeholder="Ex: Tutorial de Edição de Vídeo"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-1">
                  <span>{METRIC_TOOLTIPS.views.label}</span>
                  <div className="group relative">
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 p-2 bg-gray-900 text-white text-xs rounded-lg w-48 hidden group-hover:block">
                      {METRIC_TOOLTIPS.views.description}
                    </div>
                  </div>
                </div>
              </label>
              <input
                type="text"
                value={formData.views.toLocaleString('pt-BR')}
                onChange={(e) => handleNumberInput('views', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-1">
                  <span>{METRIC_TOOLTIPS.likes.label}</span>
                  <div className="group relative">
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 p-2 bg-gray-900 text-white text-xs rounded-lg w-48 hidden group-hover:block">
                      {METRIC_TOOLTIPS.likes.description}
                    </div>
                  </div>
                </div>
              </label>
              <input
                type="text"
                value={formData.likes.toLocaleString('pt-BR')}
                onChange={(e) => handleNumberInput('likes', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-1">
                  <span>{METRIC_TOOLTIPS.comments.label}</span>
                  <div className="group relative">
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 p-2 bg-gray-900 text-white text-xs rounded-lg w-48 hidden group-hover:block">
                      {METRIC_TOOLTIPS.comments.description}
                    </div>
                  </div>
                </div>
              </label>
              <input
                type="text"
                value={formData.comments.toLocaleString('pt-BR')}
                onChange={(e) => handleNumberInput('comments', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <div className="flex items-center space-x-1">
                <span>Taxa de Engajamento</span>
                <div className="group relative">
                  <HelpCircle className="h-4 w-4 text-gray-400" />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 p-2 bg-gray-900 text-white text-xs rounded-lg w-48 hidden group-hover:block">
                    {METRIC_TOOLTIPS.engagementRate.description}
                  </div>
                </div>
              </div>
            </label>
            <div className="px-4 py-2 bg-gray-100 rounded-lg text-gray-700">
              {engagementRate.toFixed(2)}%
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <div className="flex items-center space-x-1">
                <span>Screenshot</span>
                <div className="group relative">
                  <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 p-2 bg-gray-900 text-white text-xs rounded-lg w-48 hidden group-hover:block z-50">
                    Capture uma imagem dos analytics da sua plataforma para referência futura e comparação
                  </div>
                </div>
              </div>
            </label>
            <div 
              className={`mt-1 flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 transition-colors ${
                isDragging 
                  ? 'border-indigo-500 bg-indigo-50' 
                  : 'border-gray-300 hover:border-indigo-400'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileChange(file);
                }}
                className="hidden"
              />
              
              {formData.screenshotPreview ? (
                <div className="relative">
                  <img
                    src={formData.screenshotPreview}
                    alt="Preview"
                    className="max-h-48 rounded-lg object-contain"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFormData(prev => ({
                        ...prev,
                        screenshot: null,
                        screenshotPreview: ''
                      }));
                    }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  {isAnalyzing ? (
                    <>
                      <Loader className="mx-auto h-12 w-12 text-gray-400 animate-spin" />
                      <p className="mt-2 text-sm text-gray-600">
                        Analisando imagem...
                      </p>
                    </>
                  ) : (
                    <>
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <p className="mt-2 text-sm text-gray-600">
                        Arraste uma imagem aqui ou clique para selecionar
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        PNG, JPG, GIF até 10MB
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              {entry ? 'Salvar Alterações' : 'Adicionar Dados'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}